import { MutableRefObject, Suspense, useMemo, useRef, type ElementType, type ReactElement, type VFC } from 'react'
import { isValidElementType } from 'react-is'
import { isLazyType, isPropsArrayEqual } from './navUtils'
import { isFunction } from '../../framework/utils'
import history from '../../framework/history'
import NavErrorBoundary from '../common/NavErrorBoundary'
import { NAV_Z_1000_CLS } from '../../framework/constants'
import type { AnyState, StoreState } from '../../framework/stateTypes'
import type { NavItem } from '../../framework/types'

type IndexedStateArray = AnyState[] & Record<string, AnyState>
type BottomNavSheetsProps = {
  bottomNavItems: NavItem[] // | SparkyBottomLeftNavConfig[]
  state: StoreState
  leftNavRef: MutableRefObject<HTMLDivElement | null>
  opened: string
  setOpened: (key: string) => void
}
const BottomNavSheets: VFC<BottomNavSheetsProps> = ({ state, bottomNavItems, leftNavRef, opened, setOpened }) => {
  const bottomNavItemsWithComponents = useMemo(() => {
    return bottomNavItems.filter((item: NavItem) =>
      item.key && item.navComponent && isValidElementType(item.navComponent)
    )
  }, [bottomNavItems])

  const openedRef = useRef<string>(opened)
  openedRef.current = opened

  const prevPropsRef = useRef<IndexedStateArray>()
  const stateToProps = useMemo(() => {
    const empty = [] as AnyState[]
    if (!bottomNavItemsWithComponents?.length) {
      return empty as IndexedStateArray
    }
    const props = bottomNavItemsWithComponents.reduce((all: AnyState[], action: NavItem) => {
      const indexed = all as IndexedStateArray
      const key = action.key
      const props = key && isFunction(action.mapStateToProps) ?
        action.mapStateToProps(state) : undefined
      if (props) {
        indexed.push(props)
        indexed[key] = props
      }
      return all
    }, empty) as IndexedStateArray
    if (!props.length) {
      return empty as IndexedStateArray
    }
    if (prevPropsRef.current && isPropsArrayEqual(prevPropsRef.current, props)) {
      return prevPropsRef.current
    }
    prevPropsRef.current = props
    return props
  }, [bottomNavItemsWithComponents, state])

  const bottomSheets = useMemo(() => {
    const bottomSheets = [] as ReactElement[]
    for (const action of bottomNavItemsWithComponents) {
      const key = action.key
      action.onClick = () => {
        setOpened(openedRef.current === key ? '' : key)
      }
      const openProps = {
        isOpen: opened === key,
        setOpen(open: boolean) {
          if (open) {
            setOpened(key)
          }
          else if (openedRef.current === key) {
            setOpened(open ? key : '')
          }
        },
        open() {
          setOpened(key)
        },
        close() {
          if (openedRef.current === key) {
            setOpened('')
          }
        },
      }
      const Component = action.navComponent as ElementType
      let component = <Component
        history={history}
        leftNavRef={leftNavRef}
        isSideNavCollapsed={state.isSideNavCollapsed}
        {...stateToProps[key]}
        {...openProps}
      />
      if (isLazyType(Component)) {
        component = (
          <Suspense fallback={null}>
            {component}
          </Suspense>
        )
      }
      component = (
        <NavErrorBoundary key={key} context={`nav-panel:${key}`.trim()}>
          {component}
        </NavErrorBoundary>
      )
      bottomSheets.push(component)
    }
    return bottomSheets
  }, [bottomNavItemsWithComponents, opened, leftNavRef, state.isSideNavCollapsed, stateToProps, setOpened])

  return (
    <div
      className={`twsf-left-0 twsf-w-0 twsf-h-0 twsf-absolute twsf-overflow-hidden panwds-tw3 ${NAV_Z_1000_CLS}`}
    >{bottomSheets}</div>
  )
}

export default BottomNavSheets
